/**
 * @fileoverview Axios request interceptor.
 * @description Attaches the stored access token as a Bearer header, and a fresh correlation ID, to
 * every outgoing request, so individual API calls never need to handle these concerns themselves.
 * The device ID and location aren't sent as headers: the endpoints that take them (login, MFA,
 * password reset) carry them in the request body.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { HTTP_HEADER_CORRELATION_ID } from '@/shared/constants/http-headers.constants'
import { getStoredAccessToken } from '@/shared/lib/auth/token-storage.utils'
import { generateCorrelationId } from '@/shared/lib/device/device-identity.utils'

import type { InternalAxiosRequestConfig } from 'axios'

/**
 * @description Adds the Authorization header to a request config when an access token is stored.
 * @param {InternalAxiosRequestConfig} config - The outgoing request configuration.
 * @returns {InternalAxiosRequestConfig} The (possibly modified) request configuration.
 */
export function attachAuthToken(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const accessToken = getStoredAccessToken()

  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`)
  }

  return config
}

/**
 * @description Adds a fresh correlation ID, which the backend echoes back and logs, so one
 * request can be traced across the client and server logs.
 * @param {InternalAxiosRequestConfig} config - The outgoing request configuration.
 * @returns {InternalAxiosRequestConfig} The modified request configuration.
 */
export function attachCorrelationId(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  config.headers.set(HTTP_HEADER_CORRELATION_ID, generateCorrelationId())
  return config
}
